/* eslint-disable camelcase */
import { createSelector } from 'reselect';

import {
  getDailyMaturityExpiry,
  getFormattedDates,
  getMonthlyMaturityExpiry,
  getTwelvePM,
  getWeeklyMaturityExpiry,
} from 'components/OptionsAnalyticsDashboard/partials/charts/helpers.expiries';
import { getTimestampInMS, timeNow, toUTC } from 'helpers/day';
import { sort } from 'helpers/ramda';
import { isCallPutOptions } from 'helpers/utils';
import i18n from 'i18n/config';
import { productsSelector } from 'selectors/tradeSelectors';
import { ChartSupportedAssets } from 'types/actions/charts';
import { ContractType, Product } from 'types/IProducts';
import IState, { OptionsInfo } from 'types/Istore';
import type {
  AssetWiseExpiries,
  FormattedExpiries,
  Expiries,
} from 'types/optionsAnalytics';

import { optionsInfoSelector } from '../optionChainSeletor';

/**
 * Selector to group expiry timestamps by asset symbol.
 *
 * This selector processes a list of options information and groups their expiry timestamps
 * by the underlying asset symbol. Only call options are considered. Expiry timestamps are
 * converted to utc milliseconds and sorted in ascending order.
 *
 * @returns An object where each key is an asset symbol, and each value is an array of
 *          sorted expiry timestamps (in Unix format) associated with that asset symbol.
 * @example
 * {
 *   BTC: [1723550400000, 1723636800, 1723723200, 1723809600, 1724414400, 1725019200],
 *   ETH: [1723550400, 1723636800, 1725019200]
 * }
 */

export const assetWiseExpiriesSelector = createSelector(
  [optionsInfoSelector],
  (optionsInfo: OptionsInfo[]): AssetWiseExpiries => {
    // Find the call options information
    const callOptionsInfo = optionsInfo?.find(
      (optionInfo: OptionsInfo) => optionInfo.contract_type === ContractType.CallOptions
    );

    // If no call options info is found or it has no data, return an empty object
    if (!callOptionsInfo || !callOptionsInfo.data) {
      return {};
    }

    // Process the call options data to group expiry timestamps by asset
    return callOptionsInfo.data.reduce<AssetWiseExpiries>((assetExpiries, optionData) => {
      if (optionData.settlement_time && optionData.asset) {
        // Convert settlement times to milliseconds set at expiry time (12:00 PM)
        const timestamps = optionData.settlement_time.map(settlementTime =>
          getTimestampInMS(settlementTime)
        );
        // Sort the timestamps in ascending order
        const sortedTimestamps = sort((a: number, b: number) => a - b, timestamps);
        // Create a new object with the updated asset timestamps
        return {
          ...assetExpiries,
          [optionData.asset]: sortedTimestamps,
        };
      }
      return assetExpiries;
    }, {});
  }
);

/**
 * Selector to format expiry timestamps based on the current time.
 *
 * This selector calculates expiry timestamps relative to the current time, setting
 * specific labels for the closest expiry timestamps: daily ('d1', 'd2', 'd3').
 *
 * @returns An object mapping expiry timestamps (in seconds) to their corresponding labels ('d1', 'd2', 'd3').
 * @sample {
 *  1723550400: 'd1',
 *  1723636800: 'd2',
 *  1723723200: 'd3',
 * }
 */
export const formattedExpiriesSelector = createSelector(
  [assetWiseExpiriesSelector],
  (assetWiseExpiries: AssetWiseExpiries): FormattedExpiries => {
    // Get unique timestamps across all assets
    const allTimestamps = Object.values(assetWiseExpiries).flat();
    const uniqueTimestamps = [...new Set(allTimestamps)].sort((a, b) => a - b);

    const weekDay = 5;

    // Initialize the timestamp for 12:00 PM today
    const baseTimestamp = getTwelvePM(toUTC(timeNow()));

    // Get daily, weekly, and monthly expiry timestamps
    const dailyExpiries = getDailyMaturityExpiry(baseTimestamp);
    const weeklyExpiries = getWeeklyMaturityExpiry(weekDay, dailyExpiries.d1);
    const monthlyExpiries = getMonthlyMaturityExpiry(weekDay);

    // Combine all expiry timestamps
    const expiries = { ...dailyExpiries, ...weeklyExpiries, ...monthlyExpiries };

    // Format the expiry timestamps
    return getFormattedDates(expiries, uniqueTimestamps);
  }
);

/**
 * This selector creates a mapping of expiry timestamps to their initial selection status
 *
 * This selector combines expiry timestamps from all assets into a single
 * list and maps each expiry timestamp to a boolean indicating whether
 * it is initially selected ('d1') based on the `formattedExpiriesSelector`.
 *
 * @returns An object where keys are expiry timestamps and values
 * are booleans indicating whether the expiry is initially selected ('d1').
 * @sample {
 *  1723550400: true,
 *  1723636800: false,
 *  1723723200: false,
 * }
 */
export const initialSelectedExpiriesSelector = createSelector(
  [assetWiseExpiriesSelector, formattedExpiriesSelector],
  (
    assetWiseExpiries: AssetWiseExpiries,
    formattedExpiries: FormattedExpiries
  ): Expiries => {
    // Create a mapping of each expiry timestamp to its selection status
    const expirySelectionStatus = (expiries: number[]) =>
      expiries.reduce((statusMap, expiryTimestamp) => {
        // Check if the expiry timestamp is marked as 'd1' in the formattedExpiriesSelector
        const isInitialSelection = formattedExpiries[expiryTimestamp] === 'd1';

        // Update the status map with the expiry timestamp and its selection status
        return {
          ...statusMap,
          [expiryTimestamp]: isInitialSelection,
        };
      }, {} as Expiries);

    return Object.entries(assetWiseExpiries).reduce(
      (acc, [asset, expiries]) => ({
        ...acc,
        [asset]: expirySelectionStatus(expiries),
      }),
      { BTC: {}, ETH: {} }
    );
  }
);

export const initialSelectedAllExpiriesSelector = createSelector(
  [assetWiseExpiriesSelector],
  (assetWiseExpiries: AssetWiseExpiries): Expiries => {
    // Create a mapping of each expiry timestamp to its selection status
    const expirySelectionStatus = (expiries: number[]) =>
      expiries.reduce((statusMap, expiryTimestamp) => {
        // Update the status map with the expiry timestamp and its selection status
        return {
          ...statusMap,
          [expiryTimestamp]: true,
        };
      }, {} as Expiries);

    return Object.entries(assetWiseExpiries).reduce(
      (acc, [asset, expiries]) => ({
        ...acc,
        [asset]: expirySelectionStatus(expiries),
      }),
      {}
    );
  }
);

const optionsAnalyticsSelector = (state: IState) => state.optionsAnalytics;

// NOTE: Add other property if needed
const optionsContractsSelector = createSelector([productsSelector], products =>
  Object.values(products).reduce<Record<string, Product>>((optionsContracts, product) => {
    const { id, symbol, contract_type, settlement_time, underlying_asset, strike_price } =
      product;
    if (isCallPutOptions(contract_type) && id !== undefined && symbol !== undefined) {
      return {
        ...optionsContracts,
        [symbol]: {
          symbol,
          contract_type,
          id,
          settlement_time,
          strike_price,
          underlying_asset: {
            symbol: underlying_asset?.symbol,
          },
        },
      };
    }
    return optionsContracts;
  }, {})
);

export { optionsAnalyticsSelector, optionsContractsSelector };
/**
 * Selector to get the active expiries labels as a comma-separated string
 *
 * @sample 'D1, D2, D3'
 */
const activeExpiriesLabelSelector = (
  expiries: Expiries,
  activeAssetSymbol: ChartSupportedAssets
) =>
  createSelector(
    [formattedExpiriesSelector, assetWiseExpiriesSelector],
    (
      formattedExpiries: FormattedExpiries,
      assetWiseExpiries: AssetWiseExpiries
    ): string => {
      const activeAssetSelectedExpiries = expiries[activeAssetSymbol];
      const selectedExpiryKeys = Object.keys(activeAssetSelectedExpiries).filter(
        timestamp => activeAssetSelectedExpiries[timestamp]
      );

      // Check if no expiry is selected
      if (selectedExpiryKeys.length === 0) {
        return i18n.t('optionsAnalyticsDashboard:selectExpiry');
      }

      // Check if all expiries are selected
      if (selectedExpiryKeys.length === assetWiseExpiries[activeAssetSymbol].length) {
        return i18n.t('optionsAnalyticsDashboard:all');
      }
      // Filter selected expiries and map them to their formatted labels
      return Object.keys(expiries[activeAssetSymbol])
        .filter(timestamp => expiries[activeAssetSymbol][timestamp]) // Keep only selected expiries
        .map(timestamp => formattedExpiries[timestamp])
        .join(', ') // Combine labels into a single string
        .toUpperCase();
    }
  );

export { activeExpiriesLabelSelector };
